import { useRef, useState } from 'react';
import { Eye } from 'lucide-react';
import {
  FormGroup,
  Input as ReactstrapInput,
  Label,
  Spinner,
} from 'reactstrap';
import { Row, Col } from 'src/componentes/grid';
import { useIntl } from 'react-intl';

import Button from 'src/componentes/button';
import ContentManager from 'src/componentes/content-manager';
import DatePicker from 'src/componentes/date-picker';
import Info from 'src/componentes/info';
import InputNumber from 'src/componentes/input-number';
import Panel from 'src/componentes/panel';
import useAuth from 'src/hooks/useAuth';
import http from 'src/services/httpService';
import errorHandler from 'src/utils/error-handler';
import FrequenciaRecorrenciaFormularioGlobalLookup from 'src/lookups/FrequenciaRecorrenciaFormularioGlobalLookup';
import {
  getLookupId,
  TIPO_APLICACAO_FORMULARIO_GLOBAL,
  TIPO_TERMINO_RECORRENCIA,
} from 'src/paginas/avaliacao-desempenho/PesquisaClima/utils';
import PreviaRecorrenciaPesquisaClimaModal from './PreviaRecorrenciaPesquisaClimaModal';

const styles = {
  radioSection: {
    alignItems: 'center',
    display: 'flex',
    flexWrap: 'wrap',
    gap: 16,
    marginBottom: 16,
  },
  radioGroupLabel: {
    marginBottom: 0,
  },
};

const isEmpty = (value) => value == null || value === '';
const isPositiveNumber = (value) => Number(value) >= 1;

function AplicacaoPesquisaClimaSection({
  dataInicioModel,
  dataTerminoModel,
  disabled,
  model,
  pesquisaModel,
  previewFocusTrapPriority,
  showRequiredErrors,
}) {
  const intl = useIntl();
  const { terms: resources } = useAuth();
  const [isLoadingPrevia, setIsLoadingPrevia] = useState(false);
  const models = {
    tipoAplicacaoId: model.getModel('tipoAplicacaoId'),
    frequencia: model.getModel('frequencia'),
    periodoDisponivelRespostaDias: model.getModel(
      'periodoDisponivelRespostaDias'
    ),
    tipoTerminoRecorrenciaId: model.getModel('tipoTerminoRecorrenciaId'),
    quantidadeAplicacoes: model.getModel('quantidadeAplicacoes'),
    dataTermino: model.getModel('dataTermino'),
    podeAlterarTipoAplicacao: model.getModel('podeAlterarTipoAplicacao'),
    podeAlterarParaAplicacaoUnica: model.getModel(
      'podeAlterarParaAplicacaoUnica'
    ),
  };
  const tipoAplicacaoId =
    getLookupId(models.tipoAplicacaoId.value) ||
    TIPO_APLICACAO_FORMULARIO_GLOBAL.UNICA;
  const tipoAplicacaoInicialRef = useRef(tipoAplicacaoId);
  const tipoTerminoRecorrenciaId = getLookupId(
    models.tipoTerminoRecorrenciaId.value
  );
  const recorrente =
    tipoAplicacaoId === TIPO_APLICACAO_FORMULARIO_GLOBAL.RECORRENTE;
  const tipoAplicacaoDisabled =
    disabled || models.podeAlterarTipoAplicacao.value === false;
  const podeRestaurarAplicacaoUnicaNaoSalva =
    tipoAplicacaoInicialRef.current ===
      TIPO_APLICACAO_FORMULARIO_GLOBAL.UNICA &&
    models.podeAlterarTipoAplicacao.value !== false;
  const aplicacaoUnicaDisabled =
    tipoAplicacaoDisabled ||
    (models.podeAlterarParaAplicacaoUnica.value === false &&
      !podeRestaurarAplicacaoUnicaNaoSalva);
  const requiredError = intl.formatMessage({
    id: 'campoObrigatorioNaoInformado',
  });
  const getRequiredError = (fieldModel, invalid = isEmpty(fieldModel.value)) =>
    fieldModel.errors || (showRequiredErrors && invalid ? requiredError : null);
  const buildModel = (fieldModel, label, invalid) => {
    const error = getRequiredError(fieldModel, invalid);

    return {
      label,
      value: fieldModel.value,
      requestChange: fieldModel.requestChange,
      errors: error,
      erros: fieldModel.erros || error,
    };
  };
  const tipoAplicacaoOptions = [
    {
      id: TIPO_APLICACAO_FORMULARIO_GLOBAL.UNICA,
      label: intl.formatMessage({ id: 'labelAplicacaoUnica' }),
      info: intl.formatMessage(
        { id: 'labelAplicacaoUnicaInfo' },
        { pesquisa: resources.pesquisa }
      ),
      disabled: aplicacaoUnicaDisabled,
    },
    {
      id: TIPO_APLICACAO_FORMULARIO_GLOBAL.RECORRENTE,
      label: intl.formatMessage({ id: 'recorrente' }),
      info: intl.formatMessage(
        { id: 'labelAplicacaoRecorrenteInfo' },
        { pesquisa: resources.pesquisa }
      ),
      disabled: tipoAplicacaoDisabled,
    },
  ];
  const tipoTerminoOptions = [
    {
      id: TIPO_TERMINO_RECORRENCIA.QUANTIDADE_APLICACOES,
      label: intl.formatMessage({
        id: 'labelAposUmaQuantidadeDeAplicacoes',
      }),
    },
    {
      id: TIPO_TERMINO_RECORRENCIA.DATA_ESPECIFICA,
      label: intl.formatMessage({ id: 'labelEmUmaDataEspecifica' }),
    },
    {
      id: TIPO_TERMINO_RECORRENCIA.SEM_DATA_TERMINO,
      label: intl.formatMessage({ id: 'labelSemDataDeTermino' }),
    },
  ];

  const handleAbrirPrevia = async () => {
    if (isLoadingPrevia) return;

    setIsLoadingPrevia(true);

    try {
      const response = await http.post('/PesquisaClima/ObterPreviaAplicacoes', {
        model: {
          ...pesquisaModel.value,
          id: pesquisaModel.getModel('id').value,
          dataInicio: dataInicioModel.value,
          aplicacao: model.value,
        },
      });

      ContentManager.addContent(
        <PreviaRecorrenciaPesquisaClimaModal
          {...response.data}
          focusTrapPriority={previewFocusTrapPriority}
        />
      );
    } catch (err) {
      errorHandler(err);
    } finally {
      setIsLoadingPrevia(false);
    }
  };

  return (
    <Panel
      header={intl.formatMessage(
        { id: 'labelAplicacaoPesquisa' },
        { pesquisa: resources.pesquisa }
      )}
      subtitle={intl.formatMessage(
        { id: 'labelDefinaQuandoPesquisaFicaraDisponivel' },
        { pesquisa: resources.pesquisa }
      )}
      headerAside={
        recorrente ? (
          <Button
            type="button"
            color="link"
            size="sm"
            className="p-1"
            aria-label={intl.formatMessage({ id: 'labelPreviaRecorrencia' })}
            aria-busy={isLoadingPrevia}
            title={intl.formatMessage({ id: 'labelPreviaRecorrencia' })}
            disabled={isLoadingPrevia}
            onClick={handleAbrirPrevia}
          >
            {isLoadingPrevia ? <Spinner size="sm" /> : <Eye size={18} />}
          </Button>
        ) : undefined
      }
    >
      <div style={styles.radioSection}>
        <Label style={styles.radioGroupLabel}>
          {intl.formatMessage({ id: 'labelTipoAplicacao' })} *
        </Label>
        {tipoAplicacaoOptions.map((option) => (
          <FormGroup check inline className="mb-0" key={option.id}>
            <ReactstrapInput
              type="radio"
              name="tipoAplicacaoPesquisaClima"
              id={`tipo-aplicacao-pesquisa-clima-${option.id}`}
              checked={tipoAplicacaoId === option.id}
              disabled={option.disabled}
              onChange={() => models.tipoAplicacaoId.requestChange(option.id)}
              style={{ marginTop: 3 }}
            />
            <Label
              check
              for={`tipo-aplicacao-pesquisa-clima-${option.id}`}
              className="mb-0"
            >
              {option.label}
            </Label>
            <Info
              id={`tipo-aplicacao-pesquisa-clima-${option.id}-info`}
              className="ml-1"
              placement="right"
              text={option.info}
            />
          </FormGroup>
        ))}
      </div>

      {!recorrente && (
        <Row>
          <Col md={6}>
            <DatePicker
              timeFormat={false}
              autoFormatDate={true}
              disabled={disabled}
              model={buildModel(
                dataInicioModel,
                intl.formatMessage({ id: 'dataInicio' })
              )}
              required
              inputTextAlign="left"
            />
          </Col>
          <Col md={6}>
            <DatePicker
              timeFormat={false}
              autoFormatDate={true}
              disabled={disabled}
              model={buildModel(
                dataTerminoModel,
                intl.formatMessage({ id: 'dataTermino' })
              )}
              required
              inputTextAlign="left"
            />
          </Col>
        </Row>
      )}

      {recorrente && (
        <>
          <Row>
            <Col md={4}>
              <div className="d-flex align-items-center pb-2">
                <Label className="mb-0">
                  {intl.formatMessage({ id: 'labelDataPrimeiraAplicacao' })} *
                </Label>
                <Info
                  id="data-primeira-aplicacao-pesquisa-clima-info"
                  className="ml-1"
                  placement="right"
                  text={intl.formatMessage(
                    { id: 'labelDataPrimeiraAplicacaoInfo' },
                    { pesquisa: resources.pesquisa }
                  )}
                />
              </div>
              <DatePicker
                timeFormat={false}
                autoFormatDate={true}
                disabled={disabled}
                model={buildModel(dataInicioModel, '')}
                required
                showLabel={false}
                inputTextAlign="left"
              />
            </Col>
            <Col md={4}>
              <FrequenciaRecorrenciaFormularioGlobalLookup
                disabled={disabled}
                model={buildModel(
                  models.frequencia,
                  intl.formatMessage({ id: 'label.frequencia' })
                )}
                required
              />
            </Col>
            <Col md={4}>
              <InputNumber
                disabled={disabled}
                integer
                min={1}
                model={buildModel(
                  models.periodoDisponivelRespostaDias,
                  intl.formatMessage({
                    id: 'labelPeriodoDisponivelParaRespostaDias',
                  }),
                  !isPositiveNumber(models.periodoDisponivelRespostaDias.value)
                )}
                required
              />
            </Col>
          </Row>

          <div style={styles.radioSection}>
            <Label
              style={{
                ...styles.radioGroupLabel,
                color:
                  showRequiredErrors && !tipoTerminoRecorrenciaId
                    ? '#f05050'
                    : undefined,
              }}
            >
              {intl.formatMessage({ id: 'labelTerminoRecorrencia' })} *
            </Label>
            {tipoTerminoOptions.map((option) => (
              <FormGroup check inline className="mb-0" key={option.id}>
                <ReactstrapInput
                  type="radio"
                  name="tipoTerminoRecorrenciaPesquisaClima"
                  id={`tipo-termino-recorrencia-pesquisa-clima-${option.id}`}
                  checked={tipoTerminoRecorrenciaId === option.id}
                  disabled={disabled}
                  onChange={() =>
                    models.tipoTerminoRecorrenciaId.requestChange(option.id)
                  }
                  style={{ marginTop: 3 }}
                />
                <Label
                  check
                  for={`tipo-termino-recorrencia-pesquisa-clima-${option.id}`}
                  className="mb-0"
                >
                  {option.label}
                </Label>
              </FormGroup>
            ))}
          </div>

          {tipoTerminoRecorrenciaId ===
            TIPO_TERMINO_RECORRENCIA.QUANTIDADE_APLICACOES && (
            <Row>
              <Col md={4}>
                <InputNumber
                  disabled={disabled}
                  integer
                  min={1}
                  model={buildModel(
                    models.quantidadeAplicacoes,
                    intl.formatMessage({
                      id: 'labelQuantidadeAplicacoes',
                    }),
                    !isPositiveNumber(models.quantidadeAplicacoes.value)
                  )}
                  required
                />
              </Col>
            </Row>
          )}

          {tipoTerminoRecorrenciaId ===
            TIPO_TERMINO_RECORRENCIA.DATA_ESPECIFICA && (
            <Row>
              <Col md={4}>
                <DatePicker
                  timeFormat={false}
                  autoFormatDate={true}
                  disabled={disabled}
                  model={buildModel(
                    models.dataTermino,
                    intl.formatMessage({ id: 'labelDataTerminoRecorrencia' })
                  )}
                  required
                  inputTextAlign="left"
                />
              </Col>
            </Row>
          )}
        </>
      )}
    </Panel>
  );
}

export default AplicacaoPesquisaClimaSection;
